import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../config/api';
import { useAuth } from '../context/AuthContext';

interface CalendarEvent {
  id: string;
  title: string;
  start: string | null;
  end: string | null;
  status: string;
  customerId: string;
  notes?: string;
}

interface UpcomingEventsProps {
  maxEvents?: number;
  showViewAll?: boolean;
}

const UpcomingEvents: React.FC<UpcomingEventsProps> = ({ 
  maxEvents = 5, 
  showViewAll = true 
}) => {
  const { token } = useAuth();
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchUpcomingEvents();
  }, []);

  const fetchUpcomingEvents = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch events from today onwards
      const start = new Date().toISOString();
      const end = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(); // Next 30 days

      const response = await axios.get(
        `${API_URL}/bookings/events?start=${start}&end=${end}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Sort by start time and take only upcoming events
      const upcomingEvents = response.data
        .filter((event: CalendarEvent) => event.start && new Date(event.start) >= new Date())
        .sort((a: CalendarEvent, b: CalendarEvent) => {
          const dateA = a.start ? new Date(a.start).getTime() : 0;
          const dateB = b.start ? new Date(b.start).getTime() : 0;
          return dateA - dateB;
        })
        .slice(0, maxEvents);

      setEvents(upcomingEvents);
    } catch (err: any) {
      console.error('[UpcomingEvents] Failed to fetch:', err);
      setError(err.response?.data?.message || 'Failed to load upcoming events');
    } finally {
      setLoading(false);
    }
  };

  const formatEventTime = (startStr: string | null): string => {
    if (!startStr) return 'TBD';
    
    const start = new Date(startStr);
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);

    // Check if today
    if (start.toDateString() === now.toDateString()) {
      return `Today at ${start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    }
    
    // Check if tomorrow
    if (start.toDateString() === tomorrow.toDateString()) {
      return `Tomorrow at ${start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    }

    // Otherwise show date and time
    return start.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      hour: 'numeric', 
      minute: '2-digit' 
    });
  };

  const getStatusColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'confirmed': return '#10b981';
      case 'pending': return '#f59e0b';
      case 'cancelled': return '#ef4444';
      case 'completed': return '#6b7280';
      default: return '#6b7280';
    }
  };

  if (loading) {
    return (
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          📅 Upcoming Events
        </h3>
        <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
          Loading events...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          📅 Upcoming Events
        </h3>
        <div style={{ 
          padding: '16px', 
          backgroundColor: '#fee2e2', 
          borderRadius: '8px',
          color: '#dc2626'
        }}>
          {error}
        </div>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          📅 Upcoming Events
        </h3>
        <div style={{ 
          textAlign: 'center', 
          padding: '30px', 
          color: '#6b7280',
          backgroundColor: '#f9fafb',
          borderRadius: '8px'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>📭</div>
          <div style={{ fontWeight: '500', marginBottom: '4px' }}>No upcoming events</div>
          <div style={{ fontSize: '14px' }}>Create a booking to get started</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    }}>
      <h3 style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        📅 Upcoming Events
      </h3>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {events.map((event) => (
          <div
            key={event.id}
            style={{
              padding: '12px',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
              e.currentTarget.style.borderColor = '#d1d5db';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.borderColor = '#e5e7eb';
            }}
          >
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'flex-start',
              marginBottom: '4px'
            }}>
              <div style={{ fontWeight: '600', color: '#111827', fontSize: '15px' }}>
                {event.title}
              </div>
              <div
                style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  backgroundColor: getStatusColor(event.status) + '20',
                  color: getStatusColor(event.status),
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                {event.status}
              </div>
            </div>
            
            <div style={{ 
              fontSize: '13px', 
              color: '#6b7280',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span>🕐</span>
              {formatEventTime(event.start)}
            </div>
            
            {event.notes && (
              <div style={{ 
                fontSize: '12px', 
                color: '#9ca3af', 
                marginTop: '6px',
                fontStyle: 'italic'
              }}>
                {event.notes.length > 50 ? event.notes.substring(0, 50) + '...' : event.notes}
              </div>
            )}
          </div>
        ))}
      </div>

      {showViewAll && (
        <div style={{ marginTop: '16px', textAlign: 'center' }}>
          <a
            href="/booking"
            style={{
              color: '#2563eb',
              textDecoration: 'none',
              fontSize: '14px',
              fontWeight: '500',
              display: 'inline-block',
              padding: '8px 16px',
              borderRadius: '6px',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#eff6ff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            View All Bookings →
          </a>
        </div>
      )}
    </div>
  );
};

export default UpcomingEvents;
